import api from './client';

export type AssistantTriggerMode = 'mentions' | 'all';

export interface AssistantChat {
  chat_id: string;
  label: string;
  enabled: boolean;
  added_at: string;
}

export type AssistantDmStatus = 'pending' | 'approved' | 'rejected';

export interface AssistantDmUser {
  user_id: string;
  username: string;
  name: string;
  status: AssistantDmStatus;
  requested_at: string;
  last_seen_at: string | null;
  decided_at: string | null;
  decided_by: string | null;
}

export interface AssistantConfig {
  enabled: boolean;
  chats: AssistantChat[];
  dm_users: AssistantDmUser[];
  dm_pending: number;
  trigger_mode: AssistantTriggerMode;
  bot_username: string;
  has_bot_token: boolean;
  bot_token_preview: string;
  has_openai_api_key: boolean;
  openai_api_key_preview: string;
  openai_api_key_source: 'assistant' | 'settings' | 'worker' | 'none';
  openai_model: string;
  has_github_token: boolean;
  github_token_preview: string;
  github_repo: string;
  github_ref: string;
  has_d1_read_token: boolean;
  d1_read_token_preview: string;
  has_d1_admin_token: boolean;
  d1_admin_token_preview: string;
  snapshot_status: 'empty' | 'pending' | 'importing' | 'ready' | 'error';
  snapshot_key: string;
  snapshot_uploaded_at: string;
  snapshot_error: string;
  webhook_ready: boolean;
  updated_at: string;
}

export interface AssistantConfigInput {
  enabled?: boolean;
  trigger_mode?: AssistantTriggerMode;
  bot_token?: string;
  openai_api_key?: string;
  openai_model?: string;
  github_token?: string;
  github_repo?: string;
  github_ref?: string;
  d1_read_token?: string;
  d1_admin_token?: string;
}

export interface AssistantConnectionTest {
  bot_username: string | null;
  chats: Array<{ chat_id: string; title: string | null; type: string; error: string | null }>;
  webhook_url: string;
  pending_updates: number;
  last_error: string | null;
  snapshot_key: string | null;
  snapshot_uploaded_at: string | null;
  snapshot_status: string;
  snapshot_error: string | null;
  github: unknown;
}

export type ManualBackupStage = 'queued' | 'exporting' | 'uploading' | 'backup_ready' | 'snapshot_waiting' | 'snapshot_export' | 'snapshot_queued' | 'completed' | 'error';
export interface ManualBackupStatus {
  requestId: string;
  state: 'queued' | 'running' | 'completed' | 'error' | 'stalled';
  stage: ManualBackupStage;
  requestedAt: string;
  startedAt?: string;
  updatedAt: string;
  completedAt?: string;
  key?: string;
  sizeBytes?: number;
  sourceBytes?: number;
  uploadedBytes?: number;
  error?: string;
  events: Array<{ at: string; stage: ManualBackupStage; message?: string }>;
}

export interface AssistantSnapshotProgress {
  backup: ManualBackupStatus | null;
  snapshot: {
    status: string;
    key: string | null;
    uploaded_at: string | null;
    error: string | null;
    phase: 'empty' | 'queued' | 'uploading' | 'ingesting' | 'ready' | 'error';
    updated_at: string | null;
    import_started_at: string | null;
    workflow_id: string | null;
    workflow_status: string | null;
    parts_done: number | null;
    parts_total: number | null;
  };
}

export const getAssistantConfig = () => api.get<AssistantConfig>('/admin/assistant/config');
export const updateAssistantConfig = (data: AssistantConfigInput) =>
  api.put<AssistantConfig>('/admin/assistant/config', data);
export const addAssistantChat = (data: { chat_id: string; label?: string }) =>
  api.post<{ chats: AssistantChat[] }>('/admin/assistant/chats', data);
export const updateAssistantChat = (chatId: string, data: { label?: string; enabled?: boolean }) =>
  api.put<{ chats: AssistantChat[] }>(`/admin/assistant/chats/${encodeURIComponent(chatId)}`, data);
export const removeAssistantChat = (chatId: string) =>
  api.delete<{ chats: AssistantChat[] }>(`/admin/assistant/chats/${encodeURIComponent(chatId)}`);
export const getAssistantDmUsers = () =>
  api.get<{ users: AssistantDmUser[] }>('/admin/assistant/dm-users');
export const decideAssistantDmUser = (userId: string, status: 'approved' | 'rejected') =>
  api.put<{ users: AssistantDmUser[] }>(`/admin/assistant/dm-users/${encodeURIComponent(userId)}`, { status });
export const removeAssistantDmUser = (userId: string) =>
  api.delete<{ users: AssistantDmUser[] }>(`/admin/assistant/dm-users/${encodeURIComponent(userId)}`);
export const registerAssistantWebhook = () =>
  api.post<{ webhook_url: string; bot_username: string | null; warning?: string }>('/admin/assistant/webhook');
export const testAssistantConfig = () =>
  api.post<AssistantConnectionTest>('/admin/assistant/test');
export const refreshAssistantSnapshot = () =>
  api.post<{ backup_requested: true }>('/admin/assistant/snapshot/refresh');
export const getAssistantSnapshotProgress = () =>
  api.get<AssistantSnapshotProgress>('/admin/assistant/snapshot/status');
